import { format } from 'date-fns';
import { STATUS_CONFIG } from './constants';

// ── CDN loaders ────────────────────────────────────────────────────────────────

export function loadExcelJS() {
  return new Promise((resolve) => {
    if (window.ExcelJS) { resolve(window.ExcelJS); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js';
    script.onload = () => resolve(window.ExcelJS);
    document.head.appendChild(script);
  });
}

export function loadSocketIO() {
  return new Promise((resolve) => {
    if (window.io) { resolve(window.io); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.socket.io/4.7.5/socket.io.min.js';
    script.onload = () => resolve(window.io);
    document.head.appendChild(script);
  });
}

// ── Status helpers ─────────────────────────────────────────────────────────────

export function getStatus(row) {
  if (row.checkout && row.checkout !== 'NA') return 'CHECKOUT';
  if (row.startbreak && row.startbreak !== 'NA' && (!row.endbreak || row.endbreak === 'NA')) return 'STARTBREAK';
  if (row.endbreak && row.endbreak !== 'NA') return 'ENDBREAK';
  if (row.checkin && row.checkin !== 'NA') return 'CHECKIN';
  return 'NA';
}

export function getCoords(row) {
  const src = row.checkin;
  if (!src || src === 'NA') return null;
  const lat = parseFloat(src.lat);
  const lng = parseFloat(src.long);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

// ── Form data helpers (used by EntryModal + MapSidePanel) ─────────────────────

export function prettyKey(k) {
  return k
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\w/, (c) => c.toUpperCase())
    .trim();
}

export function flatFormData(obj, prefix = '') {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (v[0]?.url) { out[key] = v; return; }
      out[key] = v.join(', ');
    } else if (typeof v === 'object' && v.url) {
      out[key] = v;
    } else if (typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flatFormData(v, key));
    } else {
      out[key] = v;
    }
  });
  return out;
}

export function extractImages(formData) {
  const imgs = [];
  function walk(obj, prefix) {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([k, v]) => {
      const label = prefix ? `${prefix} ${k}` : k;
      if (Array.isArray(v) && v[0]?.url) {
        v.forEach((img, i) => imgs.push({ url: img.url, label: v.length > 1 ? `${label} ${i + 1}` : label }));
      } else if (v && typeof v === 'object' && v.url) {
        imgs.push({ url: v.url, label });
      } else if (v && typeof v === 'object') {
        walk(v, label);
      }
    });
  }
  walk(formData, '');
  return imgs;
}

export function getLocation(entry) {
  const loc = entry?.formData?.location;
  if (!loc) return null;
  const lat = parseFloat(loc.latitude ?? loc.lat);
  const lng = parseFloat(loc.longitude ?? loc.lng ?? loc.long);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat: lat.toFixed(6), lng: lng.toFixed(6) };
}

// ── Excel export ───────────────────────────────────────────────────────────────

export async function exportToExcel(rows, date) {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Admin Panel';
  wb.created = new Date();

  const ws = wb.addWorksheet('Field Activity', {
    views: [{ state: 'frozen', ySplit: 2 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  });

  ws.columns = [
    { key: 'no',               width: 5  },
    { key: 'name',             width: 22 },
    { key: 'username',         width: 15 },
    { key: 'role',             width: 18 },
    { key: 'project',          width: 16 },
    { key: 'region',           width: 16 },
    { key: 'status',           width: 14 },
    { key: 'checkin_time',     width: 14 },
    { key: 'checkin_selfie',   width: 40 },
    { key: 'checkin_store',    width: 40 },
    { key: 'break_start',      width: 14 },
    { key: 'break_start_selfie', width: 40 },
    { key: 'break_end',        width: 14 },
    { key: 'break_end_selfie', width: 40 },
    { key: 'checkout_time',    width: 14 },
    { key: 'checkout_selfie',  width: 40 },
    { key: 'checkout_store',   width: 40 },
    { key: 'lat',              width: 14 },
    { key: 'lng',              width: 14 },
    { key: 'mobile',           width: 15 },
    { key: 'city',             width: 16 },
  ];

  // Title row
  ws.mergeCells('A1:U1');
  const titleCell = ws.getCell('A1');
  titleCell.value = `Field Activity Report — ${date}`;
  titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF0f2137' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe6f2ff' } };
  ws.getRow(1).height = 28;

  // Header row
  const HEADERS = [
    '#', 'Full Name', 'Username', 'Role', 'Project', 'Region', 'Status',
    'Check-In Time', 'Check-In Selfie URL', 'Check-In Store URL',
    'Break Start', 'Break Start Selfie URL',
    'Break End', 'Break End Selfie URL',
    'Check-Out Time', 'Check-Out Selfie URL', 'Check-Out Store URL',
    'Latitude', 'Longitude', 'Mobile', 'City',
  ];

  const headerRow = ws.getRow(2);
  HEADERS.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFffffff' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2dccd2' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FF2dccd2' } },
      right:  { style: 'thin', color: { argb: 'FF1a8fd6' } },
    };
  });
  headerRow.height = 32;

  const fmtTime = (obj) => {
    if (!obj || obj === 'NA' || !obj.timeReported) return '';
    try { return format(new Date(obj.timeReported), 'hh:mm a'); } catch { return ''; }
  };
  const getPhoto = (obj, field) => (obj && obj !== 'NA' ? obj[field]?.data || '' : '');

  const STATUS_LABELS = {
    CHECKIN: 'Checked In', CHECKOUT: 'Checked Out',
    STARTBREAK: 'On Break', ENDBREAK: 'Break Ended', NA: 'No Activity',
  };

  rows.forEach((row, idx) => {
    const a = row.author || {};
    const loc = row.currentLocation || row.checkin;
    const status = (() => {
      if (row.checkout && row.checkout !== 'NA') return 'CHECKOUT';
      if (row.startbreak && row.startbreak !== 'NA' && (!row.endbreak || row.endbreak === 'NA')) return 'STARTBREAK';
      if (row.endbreak && row.endbreak !== 'NA') return 'ENDBREAK';
      if (row.checkin && row.checkin !== 'NA') return 'CHECKIN';
      return 'NA';
    })();

    const dataRow = ws.addRow([
      idx + 1,
      `${a.firstName || ''} ${a.lastName || ''}`.trim(),
      a.username || '',
      a.role?.title || '',
      row.project?.title || a.project?.title || '',
      row.region?.title || a.region?.title || '',
      STATUS_LABELS[status] || '',
      fmtTime(row.checkin),
      getPhoto(row.checkin, 'imageSelfie'),
      getPhoto(row.checkin, 'imageStore'),
      fmtTime(row.startbreak),
      getPhoto(row.startbreak, 'imageSelfie'),
      fmtTime(row.endbreak),
      getPhoto(row.endbreak, 'imageSelfie'),
      fmtTime(row.checkout),
      getPhoto(row.checkout, 'imageSelfie'),
      getPhoto(row.checkout, 'imageStore'),
      loc && loc !== 'NA' ? loc.lat  : '',
      loc && loc !== 'NA' ? loc.long : '',
      a.mobileNumber || '',
      [a.city, a.province].filter(Boolean).join(', '),
    ]);

    const fillColor = idx % 2 === 0 ? 'FFffffff' : 'FFf5f8fc';
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: 'Arial', size: 9 };
      cell.alignment = { vertical: 'middle', wrapText: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFe0e8f0' } } };
    });

    const statusColors = {
      CHECKIN: 'FF0a9e74', CHECKOUT: 'FFc06000',
      STARTBREAK: 'FF7b1fa2', ENDBREAK: 'FF2dccd2', NA: 'FF6b7f8e',
    };
    dataRow.getCell(7).font = { name: 'Arial', size: 9, bold: true, color: { argb: statusColors[status] } };

    [9, 10, 12, 14, 16, 17].forEach((col) => {
      const cell = dataRow.getCell(col);
      if (cell.value) {
        cell.value = { text: 'View Photo', hyperlink: cell.value };
        cell.font = { name: 'Arial', size: 9, color: { argb: 'FF2dccd2' }, underline: true };
      }
    });

    dataRow.height = 20;
  });

  ws.autoFilter = { from: 'A2', to: 'U2' };

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a2 = document.createElement('a');
  a2.href = url;
  a2.download = `field_activity_${date}.xlsx`;
  a2.click();
  URL.revokeObjectURL(url);
}
